'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Testimonial {
  id: string;
  name: string;
  title: string;
  hospital: string;
  image: string;
  alt: string;
  quote: string;
  videoUrl?: string;
}

interface CaseStudy {
  id: string;
  hospital: string;
  location: string;
  nicuLevel: string;
  implementation: string;
  results: {
    metric: string;
    improvement: string;
  }[];
  image: string;
  alt: string;
}

const SocialProof = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [currentCaseStudy, setCurrentCaseStudy] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const testimonials: Testimonial[] = [
  {
    id: '1',
    name: 'Dr. Guruprasad G',
    title: 'MD, DCH, DNB, DM, FNNF, MNAMS, Professor, HOD Department of Neonatology',
    hospital: 'JJM Medical College, Davangere',
    image: "/assets/images/GP-1767459586727.png",
    alt: 'Dr. Guruprasad G - Professor, HOD Department of Neonatology',
    quote: 'REVIVE automatically manages the entire treatment. Keeping track of the progress of the treatment is also very easy on REVIVE. We found REVIVE fits well into our NICU and works great!'
  },
  {
    id: '2',
    name: 'Dr. Bhuvaneshwari Yelamali',
    title: 'MD, DCH, Medical Superintendent, Professor of Pediatrics',
    hospital: 'S. Nijalingappa Medical College, Bagalkot',
    image: "/assets/images/BY-1767459764331.png",
    alt: 'Dr. Bhuvaneshwari Yelamali - Medical Superintendent, Professor of Pediatrics',
    quote: 'Our experience has been very good with REVIVE system. The system accurately maintains the temperature and beautifully displays the temperature trend over the entire duration. The installation and application support has been very good and we have been using the system since Dec 2020.'
  },
  {
    id: '3',
    name: 'Dr. Kiran More',
    title: 'MD, DCH, Neonatologist',
    hospital: 'While at Wadia Hospital, Mumbai',
    image: "/assets/images/KM-1767458876952.jpg",
    alt: 'Dr. Kiran More - Neonatologist',
    quote: 'REVIVE has transformed our approach to therapeutic hypothermia. The automated temperature control and comprehensive monitoring give us confidence in delivering optimal neuroprotective care to our most vulnerable patients.'
  }];


  const caseStudies: CaseStudy[] = [
  {
    id: '1',
    hospital: 'A leading Children\'s hospital in Mumbai',
    location: 'Mumbai, India',
    nicuLevel: 'Level III NICU',
    implementation: 'Full NICU deployment - 18 beds',
    results: [
    { metric: 'aEEG Setup Time', improvement: '5 min' },
    { metric: 'Treatment Automation', improvement: '100%' },
    { metric: 'Temperature Accuracy', improvement: '±0.1°C' },
    { metric: 'Staff Training Time', improvement: '-70%' }],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18545e41a-1766910509183.png",
    alt: 'Modern Indian NICU facility with REVIVE cooling system and monitoring equipment'
  },
  {
    id: '2',
    hospital: 'JJM Medical College',
    location: 'Davangere, India',
    nicuLevel: 'Level III NICU',
    implementation: 'Phased rollout - 15 beds',
    results: [
    { metric: 'HIE Outcomes', improvement: '65% better' },
    { metric: 'NICU Integration', improvement: 'Seamless' },
    { metric: 'Treatment Tracking', improvement: 'Real-time' },
    { metric: 'Cost Efficiency', improvement: '40% savings' }],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18545e41a-1766910509183.png",
    alt: 'Indian medical college NICU with REVIVE device showing patient monitoring screens'
  }];


  const handlePrevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const handleNextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const handlePrevCaseStudy = () => {
    setCurrentCaseStudy((prev) => (prev - 1 + caseStudies.length) % caseStudies.length);
  };

  const handleNextCaseStudy = () => {
    setCurrentCaseStudy((prev) => (prev + 1) % caseStudies.length);
  };

  return (
    <section id="social-proof" className="py-24 bg-surface">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Trusted by Leading Institutions Badge - Moved above Major Customers */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-2xl">
            <Icon name="StarIcon" size={20} />
            <span className="font-semibold text-lg">Trusted by Leading Institutions</span>
          </div>
        </div>

        {/* Major Customers Section */}
        <div className="mb-16">
          <h3 className="font-bold font-heading text-foreground mb-8 text-center text-[32px]">
            Our Major Customers
          </h3>
          <p className="text-center text-muted-foreground mb-12 max-w-3xl mx-auto">
            Leading hospitals across India trust REVIVE for neonatal neuroprotective care
          </p>
          
          {/* Scrolling Container */}
          <div className="relative overflow-hidden">
            {/* Gradient Overlays */}
            <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
            
            {/* Scrolling Content */}
            <div className="flex gap-6 animate-scroll hover:pause-animation">
              {/* First Set */}
              {[
              { name: 'P.D Hinduja Hospital', location: 'Mumbai', logo: '/assets/images/Screenshot_2025-12-29_at_9.09.27_PM-1767022775755.png' },
              { name: 'Bai Jerbai Wadia Hospital for Children', location: 'Mumbai', logo: '/assets/images/Screenshot_2025-12-29_at_9.04.02_PM-1767022457226.png' },
              { name: 'Ankura Hospitals', location: 'Bhubaneshwar, Khammam', logo: '/assets/images/Screenshot_2025-12-29_at_9.02.43_PM-1767022391602.png' },
              { name: 'PGIMER', location: 'Chandigarh', logo: '/assets/images/Screenshot_2025-12-29_at_9.08.33_PM-1767022725849.png' },
              { name: 'KIMS Hospital', location: 'Secunderabad', logo: '/assets/images/Screenshot_2025-12-29_at_9.06.57_PM-1767022626202.png' },
              { name: 'Niloufer Hospital for Women and Children', location: 'Hyderabad', logo: '/assets/images/Screenshot_2025-12-29_at_9.11.03_PM-1767022874380.png' },
              { name: 'J.J.M Medical College & Hospital', location: 'Davangere', logo: '/assets/images/Screenshot_2025-12-29_at_9.12.35_PM-1767022965876.png' },
              { name: 'St. Johns Hospital', location: 'Bengaluru', logo: '/assets/images/hospitals/stjohns-logo.png' },
              { name: 'GMERS Medical College & Hospital', location: 'Ahmedabad', logo: '/assets/images/hospitals/gmers-logo.png' },
              { name: 'KEM Hospital', location: 'Pune', logo: '/assets/images/hospitals/kem-logo.png' },
              { name: 'Bharati Hospital', location: 'Pune', logo: '/assets/images/hospitals/bharati-logo.png' }].
              map((hospital, index) =>
              <div key={`first-${index}`} className="flex-shrink-0 w-64 bg-card rounded-2xl p-6 shadow-card hover:shadow-lg transition-all duration-250 ease-out text-center group">
                  <div className="h-32 flex items-center justify-center mb-4">
                    <div className="w-32 h-32 rounded-2xl bg-white flex items-center justify-center p-2 group-hover:scale-105 transition-transform duration-250">
                      <AppImage
                      src={hospital.logo}
                      alt={`${hospital.name} logo`}
                      className="w-full h-full object-contain" />
                    </div>
                  </div>
                  <h4 className="font-bold text-foreground text-base mb-1">{hospital.name}</h4>
                  <p className="text-sm text-muted-foreground">{hospital.location}</p>
                </div>
              )}
              {/* Duplicate Set for Seamless Loop */}
              {[
              { name: 'P.D Hinduja Hospital', location: 'Mumbai', logo: '/assets/images/Screenshot_2025-12-29_at_9.09.27_PM-1767022775755.png' },
              { name: 'Bai Jerbai Wadia Hospital for Children', location: 'Mumbai', logo: '/assets/images/Screenshot_2025-12-29_at_9.04.02_PM-1767022457226.png' },
              { name: 'Ankura Hospitals', location: 'Bhubaneshwar, Khammam', logo: '/assets/images/Screenshot_2025-12-29_at_9.02.43_PM-1767022391602.png' },
              { name: 'PGIMER', location: 'Chandigarh', logo: '/assets/images/Screenshot_2025-12-29_at_9.08.33_PM-1767022725849.png' },
              { name: 'KIMS Hospital', location: 'Secunderabad', logo: '/assets/images/Screenshot_2025-12-29_at_9.06.57_PM-1767022626202.png' },
              { name: 'Niloufer Hospital for Women and Children', location: 'Hyderabad', logo: '/assets/images/Screenshot_2025-12-29_at_9.11.03_PM-1767022874380.png' },
              { name: 'J.J.M Medical College & Hospital', location: 'Davangere', logo: '/assets/images/Screenshot_2025-12-29_at_9.12.35_PM-1767022965876.png' },
              { name: 'St. Johns Hospital', location: 'Bengaluru', logo: '/assets/images/hospitals/stjohns-logo.png' },
              { name: 'GMERS Medical College & Hospital', location: 'Ahmedabad', logo: '/assets/images/hospitals/gmers-logo.png' },
              { name: 'KEM Hospital', location: 'Pune', logo: '/assets/images/hospitals/kem-logo.png' },
              { name: 'Bharati Hospital', location: 'Pune', logo: '/assets/images/hospitals/bharati-logo.png' }].
              map((hospital, index) =>
              <div key={`second-${index}`} className="flex-shrink-0 w-64 bg-card rounded-2xl p-6 shadow-card hover:shadow-lg transition-all duration-250 ease-out text-center group">
                  <div className="h-32 flex items-center justify-center mb-4">
                    <div className="w-32 h-32 rounded-2xl bg-white flex items-center justify-center p-2 group-hover:scale-105 transition-transform duration-250">
                      <AppImage
                      src={hospital.logo}
                      alt={`${hospital.name} logo`}
                      className="w-full h-full object-contain" />
                    </div>
                  </div>
                  <h4 className="font-bold text-foreground text-base mb-1">{hospital.name}</h4>
                  <p className="text-sm text-muted-foreground">{hospital.location}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Clinicians Love REVIVE Section - Moved after Major Customers */}
        <div className="text-center mb-16">
          <h2 className="lg:text-5xl font-bold font-heading text-foreground mb-6 text-[32px]">
            Clinicians Love REVIVE - "It Fits Seamlessly into Our NICU."
          </h2>

          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Hear directly from neonatologists and NICU directors at leading Indian hospitals about their experience with REVIVE technology.
          </p>
        </div>

        {/* Testimonials Carousel - One frame at a time */}
        <div className="bg-card rounded-2xl shadow-card overflow-hidden mb-16">
          <div className="max-w-4xl mx-auto p-8">
            {/* Doctor Photo - Centered at Top */}
            <div className="flex justify-center mb-6">
              <div className="relative w-64 h-64 rounded-full overflow-hidden bg-surface shadow-lg">
                <AppImage
                  key={`testimonial-${currentTestimonial}`}
                  src={testimonials[currentTestimonial].image}
                  alt={testimonials[currentTestimonial].alt}
                  className="w-full h-full object-cover" />

              </div>
            </div>

            {/* Doctor Details - Centered */}
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold font-heading mb-2 text-foreground">
                {testimonials[currentTestimonial].name}
              </h3>
              <p className="text-base text-muted-foreground mb-1">
                {testimonials[currentTestimonial].title}
              </p>
              <p className="text-base text-primary font-semibold">
                {testimonials[currentTestimonial].hospital}
              </p>
            </div>

            {/* Testimonial Quote - Centered */}
            <div className="mb-8">
              <div className="flex justify-center mb-4">
                <Icon name="ChatBubbleLeftRightIcon" size={48} className="text-primary" />
              </div>
              <blockquote className="text-xl text-foreground leading-relaxed text-center max-w-3xl mx-auto">
                "{testimonials[currentTestimonial].quote}"
              </blockquote>
            </div>

            {/* Navigation Controls at Bottom - Centered */}
            <div className="flex flex-col items-center space-y-4">
              <div className="flex items-center space-x-2">
                {testimonials.map((_, index) =>
                <button
                  key={index}
                  onClick={isHydrated ? () => setCurrentTestimonial(index) : undefined}
                  className={`w-2 h-2 rounded-full transition-all duration-250 ease-out ${
                  index === currentTestimonial ?
                  'bg-primary w-8' : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'}`
                  }
                  aria-label={`View testimonial ${index + 1}`} />

                )}
              </div>

              <div className="flex items-center space-x-4">
                <button
                  onClick={isHydrated ? handlePrevTestimonial : undefined}
                  className="p-3 rounded-2xl bg-surface hover:bg-primary hover:text-white transition-all duration-250 ease-out shadow-md"
                  aria-label="Previous testimonial">

                  <Icon name="ChevronLeftIcon" size={24} className="text-current" />
                </button>
                <span className="text-sm text-muted-foreground font-medium">
                  {currentTestimonial + 1} / {testimonials.length}
                </span>
                <button
                  onClick={isHydrated ? handleNextTestimonial : undefined}
                  className="p-3 rounded-2xl bg-surface hover:bg-primary hover:text-white transition-all duration-250 ease-out shadow-md"
                  aria-label="Next testimonial">

                  <Icon name="ChevronRightIcon" size={24} className="text-current" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h3 className="font-bold font-heading text-foreground mb-8 text-center text-[32px]">
            Hospital Implementation Success Stories
          </h3>

          <div className="bg-card rounded-2xl shadow-card overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-8 p-8">
              <div className="relative h-80 rounded-2xl overflow-hidden">
                <AppImage
                  src={caseStudies[currentCaseStudy].image}
                  alt={caseStudies[currentCaseStudy].alt}
                  className="w-full h-full object-cover" />

              </div>

              <div>
                <div className="mb-6">
                  <h4 className="text-2xl font-bold font-heading text-foreground mb-2">
                    {caseStudies[currentCaseStudy].hospital}
                  </h4>
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                    <span className="flex items-center space-x-1">
                      <Icon name="MapPinIcon" size={16} />
                      <span>{caseStudies[currentCaseStudy].location}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="BuildingOffice2Icon" size={16} />
                      <span>{caseStudies[currentCaseStudy].nicuLevel}</span>
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    {caseStudies[currentCaseStudy].implementation}
                  </p>
                </div>

                <div className="space-y-4">
                  <h5 className="font-bold text-foreground">Implementation Results:</h5>
                  {caseStudies[currentCaseStudy].results.map((result, index) =>
                  <div key={index} className="flex items-center justify-between p-3 bg-surface rounded-lg">
                      <span className="text-sm text-muted-foreground">{result.metric}</span>
                      <span className="text-lg font-bold text-success">{result.improvement}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between mt-6">
                  <div className="flex items-center space-x-2">
                    {caseStudies.map((_, index) =>
                    <button
                      key={index}
                      onClick={isHydrated ? () => setCurrentCaseStudy(index) : undefined}
                      className={`w-2 h-2 rounded-full transition-all duration-250 ease-out ${
                      index === currentCaseStudy ?
                      'bg-primary w-8' : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'}`
                      }
                      aria-label={`View case study ${index + 1}`} />

                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={isHydrated ? handlePrevCaseStudy : undefined}
                      className="p-2 rounded-2xl bg-surface hover:bg-muted transition-colors duration-250 ease-out"
                      aria-label="Previous case study">

                      <Icon name="ChevronLeftIcon" size={20} className="text-foreground" />
                    </button>
                    <button
                      onClick={isHydrated ? handleNextCaseStudy : undefined}
                      className="p-2 rounded-2xl bg-surface hover:bg-muted transition-colors duration-250 ease-out"
                      aria-label="Next case study">

                      <Icon name="ChevronRightIcon" size={20} className="text-foreground" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-card rounded-lg p-6 text-center">
            <div className="text-4xl font-bold font-heading text-primary mb-2">400+</div>
            <p className="text-muted-foreground">Neonates Successfully Treated</p>
          </div>
          <div className="bg-card rounded-lg p-6 text-center">
            <div className="text-4xl font-bold font-heading text-primary mb-2">40+</div>
            <p className="text-muted-foreground">Leading Hospital Partners</p>
          </div>
          <div className="bg-card rounded-lg p-6 text-center">
            <div className="text-4xl font-bold font-heading text-primary mb-2">99.2%</div>
            <p className="text-muted-foreground">Treatment Success Rate</p>
          </div>
        </div>
      </div>
    </section>);

};

export default SocialProof;